import { AxiosError } from 'axios';
import { action } from 'typesafe-actions';
import { EOrderTab } from '../long-steel-orders/types';
import { ECustomerTypeView } from '../user/types';
import {
  Billing,
  IBilledOrderData,
  IBilledOrderTracking,
  ICgCertificateResponse,
  INewLongSteelOrdersResponse,
  IOpenOrderData,
  IQCFileResponse,
  ITrackingFlatSteel,
  ITrackingLongSteel,
  IUpdateItems,
  IXMLResponse,
  SalesOrderItem,
  SalesOrderItemTable,
  Types
} from './types';

export const loadNewLongSteelOrdersFromLocalStorage = () =>
  action(Types.LOAD_NEW_LONG_STEEL_ORDERS_FROM_LOCAL_STORAGE, {});

export const loadNewLongSteelOrders = ({
  states,
  customerIds,
  customerViewType,
  startDate,
  endDate,
  needToLoadCustomer,
  isManager,
  orderType
}: {
  states: string[];
  customerIds: string[];
  customerViewType: ECustomerTypeView;
  startDate?: string;
  endDate?: string;
  needToLoadCustomer?: boolean;
  isManager: boolean;
  orderType?: EOrderTab;
}) =>
  action(Types.LOAD_NEW_LONG_STEEL_ORDERS, {
    states,
    customerIds,
    customerViewType,
    startDate,
    endDate,
    needToLoadCustomer,
    isManager,
    orderType
  });

export const loadNewLongSteelOrdersSuccess = (
  ordersResponse: INewLongSteelOrdersResponse
) => action(Types.LOAD_NEW_LONG_STEEL_ORDERS_SUCCESS, { ordersResponse });
export const loadNewLongSteelOrdersFailure = (
  error: unknown,
  bpError?: boolean
) => action(Types.LOAD_NEW_LONG_STEEL_ORDERS_FAILURE, { error, bpError });

export const loadNewTrackingFlatSteel = (openOrderItem: IOpenOrderData) =>
  action(Types.LOAD_NEW_TRACKING_FLAT_STEEL, { openOrderItem });
export const loadNewTrackingFlatSteelSuccess = (
  openOrderItem: IOpenOrderData,
  trackingFlatSteel: ITrackingFlatSteel
) =>
  action(Types.LOAD_NEW_TRACKING_FLAT_STEEL_SUCCESS, {
    openOrderItem,
    trackingFlatSteel
  });
export const loadNewTrackingFlatSteelFailure = (
  openOrderItem: IOpenOrderData,
  error: unknown
) =>
  action(Types.LOAD_NEW_TRACKING_FLAT_STEEL_FAILURE, { openOrderItem, error });

export const loadNewTrackingLongSteel = (
  openOrderItemId: string,
  customerId: string,
  salesOrder: number,
  salesOrderItem: number
) =>
  action(Types.LOAD_NEW_TRACKING_LONG_STEEL, {
    openOrderItemId,
    customerId,
    salesOrder,
    salesOrderItem
  });
export const loadNewTrackingLongSteelSuccess = (
  openOrderItemId: string,
  trackingLongSteel: ITrackingLongSteel
) =>
  action(Types.LOAD_NEW_TRACKING_LONG_STEEL_SUCCESS, {
    openOrderItemId,
    trackingLongSteel
  });
export const loadNewTrackingLongSteelFailure = (
  openOrderItemId: string,
  error: unknown
) =>
  action(Types.LOAD_NEW_TRACKING_LONG_STEEL_FAILURE, {
    openOrderItemId,
    error
  });

export const loadNewBilledOrderTracking = (billedOrderItem: IBilledOrderData) =>
  action(Types.LOAD_NEW_BILLED_ORDER_TRACKING, { billedOrderItem });
export const loadNewBilledOrderTrackingSuccess = (
  billedOrderItem: IBilledOrderData,
  billedOrderTracking: IBilledOrderTracking
) =>
  action(Types.LOAD_NEW_BILLED_ORDER_TRACKING_SUCCESS, {
    billedOrderItem,
    billedOrderTracking
  });
export const loadNewBilledOrderTrackingFailure = (
  billedOrderItem: IBilledOrderData,
  error: unknown
) =>
  action(Types.LOAD_NEW_BILLED_ORDER_TRACKING_FAILURE, {
    billedOrderItem,
    error
  });

export const loadNewQCFile = (orders: SalesOrderItem[]) =>
  action(Types.LOAD_NEW_QC_FILE, { orders });
export const loadNewQCFileSuccess = (
  orders: SalesOrderItem[],
  qcFileResponse: IQCFileResponse
) => action(Types.LOAD_NEW_QC_FILE_SUCCESS, { orders, qcFileResponse });
export const loadNewQCFileFailure = (error: unknown) =>
  action(Types.LOAD_NEW_QC_FILE_FAILURE, { error });
export const downloadNewQCFile = (order: SalesOrderItem) =>
  action(Types.DOWNLOAD_NEW_QC_FILE, { order });

export const loadNewNFFile = ({ billings }: { billings?: Billing[] }) =>
  action(Types.LOAD_NEW_NF_FILE, { billings });
export const loadNewNFFileSuccess = ({ billings }: { billings: Billing[] }) =>
  action(Types.LOAD_NEW_NF_FILE_SUCCESS, {
    billings
  });
export const loadNewNFFileFailure = (error: unknown) =>
  action(Types.LOAD_NEW_NF_FILE_FAILURE, { error });
export const downloadNewNFFile = (order: SalesOrderItem) =>
  action(Types.DOWNLOAD_NEW_NF_FILE, { order });

export const loadNewShippingNoteFile = (orders: SalesOrderItem[]) =>
  action(Types.LOAD_NEW_SHIPPING_NOTE_FILE, { orders });
export const loadNewShippingNoteFileSuccess = (orders: SalesOrderItem[]) =>
  action(Types.LOAD_NEW_SHIPPING_NOTE_FILE_SUCCESS, {
    orders
  });
export const loadNewShippingNoteFileFailure = (error: unknown) =>
  action(Types.LOAD_NEW_SHIPPING_NOTE_FILE_FAILURE, { error });
export const downloadShippingNoteFile = (order: SalesOrderItem) =>
  action(Types.DOWNLOAD_NEW_SHIPPING_NOTE_FILE, { order });

export const clearNewOrdersState = (customerViewType?: ECustomerTypeView) =>
  action(Types.CLEAR_NEW_ORDERS_STATE, { customerViewType });

export const cgNewQcRequest = (order: IBilledOrderData) =>
  action(Types.CG_NEW_QC_FILE_REQUEST, { order });

export const cgNewQcRequestSuccess = (
  order: IBilledOrderData,
  cgCqResponse: ICgCertificateResponse
) =>
  action(Types.CG_NEW_QC_FILE_REQUEST_SUCCESS, {
    order,
    cgCqResponse
  });

export const cgNewQcRequestFailure = (order: IBilledOrderData) =>
  action(Types.CG_NEW_QC_FILE_REQUEST_FAILURE, { order });

export const cgNewBulkQcRequest = (order: IBilledOrderData) =>
  action(Types.CG_NEW_QC_FILE_REQUEST, { order });

export const cgNewBulkRequestSuccess = (
  order: IBilledOrderData,
  cgCqResponse: ICgCertificateResponse
) =>
  action(Types.CG_NEW_QC_FILE_REQUEST_SUCCESS, {
    order,
    cgCqResponse
  });

export const cgNewBulkQcRequestFailure = (order: IBilledOrderData) =>
  action(Types.CG_NEW_QC_FILE_REQUEST_FAILURE, { order });

export const cgNewQcDownload = (order: IBilledOrderData) =>
  action(Types.CG_NEW_QC_FILE_DOWNLOAD, { order });

export const updateOrdersFiltered = ({
  orders
}: {
  orders: SalesOrderItemTable[];
}) =>
  action(Types.UPDATE_FILTERED_ORDERS, {
    ordersFiltered: orders
  });

export const loadUpdateOrderItems = ({
  itemsSelected,
  itemsToUpdate,
  cancelMessage,
  typeAction
}: Partial<IUpdateItems>) =>
  action(Types.LOAD_UPDATE_ITEMS, {
    updateItems: { itemsSelected, itemsToUpdate, cancelMessage, typeAction }
  });
export const loadUpdateOrderItemsSuccess = (
  updateItems: Partial<IUpdateItems>
) =>
  action(Types.LOAD_UPDATE_ITEMS_SUCCESS, {
    updateItems
  });
export const loadUpdateOrderItemsFailure = () =>
  action(Types.LOAD_UPDATE_ITEMS_FAILURE);

export const validationUpdateItems = ({
  itemsSelected,
  typeAction
}: Partial<IUpdateItems>) =>
  action(Types.VALIDATION_UPDATE_ITEMS, {
    updateItems: { itemsSelected, typeAction }
  });
export const validationUpdateItemsSuccess = ({
  updateItems
}: {
  updateItems: Partial<IUpdateItems>;
}) =>
  action(Types.VALIDATION_UPDATE_ITEMS_SUCCESS, {
    updateItems
  });
export const validationUpdateItemsFailure = () =>
  action(Types.VALIDATION_UPDATE_ITEMS_FAILURE);

export const loadOptionsUpdateItems = (updateItems: Partial<IUpdateItems>) =>
  action(Types.VALIDATION_UPDATE_ITEMS, { updateItems });
export const loadOptionsUpdateItemsSuccess = (
  updateItems: Partial<IUpdateItems>
) =>
  action(Types.VALIDATION_UPDATE_ITEMS_SUCCESS, {
    updateItems
  });
export const loadOptionsUpdateItemsFailure = () =>
  action(Types.VALIDATION_UPDATE_ITEMS_FAILURE);

export const loadOrderDetail = ({
  orderId,
  customerId,
  customerType,
  onSuccess
}: {
  orderId: number;
  customerId: string;
  customerType: ECustomerTypeView;
  onSuccess: () => void;
}) =>
  action(Types.LOAD_NEW_LONG_STEEL_ORDER_DETAIL, {
    orderId,
    customerId,
    customerType,
    onSuccess
  });
export const loadOrderDetailSuccess = (orderDetail: SalesOrderItem[]) =>
  action(Types.LOAD_NEW_LONG_STEEL_ORDER_DETAIL_SUCCESS, {
    orderDetail
  });
export const loadOrderDetailFailure = () =>
  action(Types.LOAD_NEW_LONG_STEEL_ORDER_DETAIL_FAILURE);

export const downloadOrderItems = ({
  downloadsItemsSelected
}: {
  downloadsItemsSelected: SalesOrderItem[];
}) =>
  action(Types.DOWNLOADS_ITEMS, {
    downloadsItemsSelected
  });

export const repeatOrderItems = ({
  loadingRepeatOrder
}: {
  loadingRepeatOrder: boolean;
}) =>
  action(Types.REPEAT_ORDERS, {
    updateItems: {
      loading: {
        repeatOrders: loadingRepeatOrder
      }
    }
  });

export const updateTableType = ({ tableType }: { tableType: EOrderTab }) => {
  return action(Types.UPADTE_TABLE_TYPE, {
    tableType
  });
};

export const loadXml = (billingNumber: string) =>
  action(Types.LOAD_XML, { billingNumber });

export const loadXmlSuccess = (
  billingNumber: string,
  downloadXmlResponse: IXMLResponse
) => action(Types.LOAD_XML_SUCCESS, { billingNumber, downloadXmlResponse });

export const loadXmlFailure = (billingNumber: string, error: AxiosError) =>
  action(Types.LOAD_XML_FAILURE, { billingNumber, error });
